import React, { useEffect, useMemo } from 'react';
import type { Message } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { hasGracePeriodAtom } from '~entities/order';
import {
  getMinDownPayment,
  loanMath,
  getMaxDownPayment,
  getMaxGracePeriodInMonths,
  getRangeMessage,
} from '~entities/product';
import type { ProductParams } from '~entities/product';
import { getInstallmentPeriodOptions } from '~entities/product';

import { phMoneyProps } from '~shared/constants/ph-money-props';
import { useOnMountEffect } from '~shared/hooks';
import { Button } from '~shared/ui/button';
import { validators } from '~shared/validators';

import { FormFieldset } from '@breeze-platform-ui/form';
import { RadioGroup, InputMoney } from '@breeze/rhf-adapters';
import { useAtomValue } from 'jotai';

import starSvg from './images/star.svg';

import {
  DOWNPAYMENT_LABEL,
  DOWNPAYMENT_REQUIRED_ERROR,
  PERIOD_REQUIRED_ERROR,
} from '../constants';
import { validateDownPaymentRange } from '../lib';

export type FormValues = {
  downPayment: string;
  period: string;
};

type Props = {
  totalPrice: number;
  loanParams: ProductParams;
  disableSubmit?: boolean;
};

export const SetupLoan = React.memo(
  ({ totalPrice, loanParams, disableSubmit }: Props) => {
    const { control, watch, formState, trigger, setValue, getValues } =
      useFormContext<FormValues>();
    const {
      minLoanAmount,
      maxLoanAmount,
      minDownPaymentPercent,
      maxDownPaymentPercent,
    } = loanParams;

    const hasGracePeriod = useAtomValue(hasGracePeriodAtom);
    const downPayment = watch('downPayment');
    const loanAmount = !Number.isNaN(downPayment)
      ? loanMath.calculateLoanAmount(totalPrice, +downPayment)
      : 0;

    const initialDownPayment = getMinDownPayment(totalPrice, {
      maxLoanAmount,
      minDownPaymentPercent,
      maxDownPaymentPercent,
    });

    const options = useMemo(
      () =>
        getInstallmentPeriodOptions(
          totalPrice,
          hasGracePeriod,
          loanParams,
          loanAmount || 0
        ),
      [totalPrice, hasGracePeriod, loanParams, loanAmount]
    );

    const maxZeroPlanMonths = useMemo(
      () =>
        hasGracePeriod
          ? getMaxGracePeriodInMonths(totalPrice, loanParams.termsInMonths)
          : 0,
      [hasGracePeriod, totalPrice, loanParams.termsInMonths]
    );

    useOnMountEffect(() => {
      if (!getValues('downPayment')) {
        setValue('downPayment', `${initialDownPayment}`);
      }

      if (!getValues('period')) {
        setValue('period', options[0].value);
      }
    });

    // we need to revalidate downPayment when totalPrice changes
    useEffect(() => {
      trigger('downPayment');
    }, [totalPrice, loanParams, trigger]);

    const shouldShowDownPayment = maxDownPaymentPercent > 0;
    const downPaymentValid =
      !shouldShowDownPayment || !formState.errors.downPayment;
    const downPaymentMessage =
      shouldShowDownPayment &&
      getRangeMessage(
        getMinDownPayment(totalPrice, {
          maxLoanAmount,
          minDownPaymentPercent,
          maxDownPaymentPercent,
        }),
        getMaxDownPayment(totalPrice, {
          minLoanAmount,
          minDownPaymentPercent,
          maxDownPaymentPercent,
        })
      );

    return (
      <FormFieldset>
        {shouldShowDownPayment && (
          <Controller
            name="downPayment"
            control={control}
            rules={{
              required: DOWNPAYMENT_REQUIRED_ERROR,
              validate: (value) =>
                // react-hook-form doesn't accept ReactNode as error, but FormRow does -> have to use any
                validateDownPaymentRange(+value, totalPrice, {
                  maxLoanAmount,
                  minLoanAmount,
                  minDownPaymentPercent,
                  maxDownPaymentPercent,
                }) as Message,
            }}
            render={(props) => (
              <InputMoney
                {...props}
                {...phMoneyProps}
                cleanable
                currencyInValue
                withValidityMark={false}
                label={DOWNPAYMENT_LABEL}
                emptyValue={initialDownPayment}
                message={!props.fieldState.error && downPaymentMessage}
              />
            )}
          />
        )}
        {!Number.isNaN(loanAmount) && loanAmount > 0 && downPaymentValid && (
          <Controller
            name="period"
            control={control}
            rules={{
              validate: validators.required({ text: PERIOD_REQUIRED_ERROR }),
            }}
            render={(props) => (
              <RadioGroup
                {...props}
                qaLabel="period-radio"
                vertical
                wide
                options={options as any}
              />
            )}
          />
        )}
        {hasGracePeriod && (
          <div className="relative mb-5 flex items-center justify-between gap-4 overflow-hidden rounded-2xl bg-[#BBF452] px-2 py-2.5">
            <div className="flex flex-col gap-2">
              <span className="inline-flex w-fit rounded-full bg-white px-2.5 py-0.5 text-[11px] text-black">
                Zero Plan: {maxZeroPlanMonths} months
              </span>
              <p className="max-w-[217px] px-2 text-[13px] text-black">
                No interest if the full loan is paid within the Zero Plan
                period.
              </p>
            </div>
            <div className="absolute right-0 top-[-18px] flex shrink-0 items-center justify-center">
              <img src={starSvg} alt="" className="h-[123px] w-[125px]" />
            </div>
          </div>
        )}
        <Button
          type="submit"
          size="l"
          wide
          disabled={!formState.isValid || disableSubmit}
        >
          Apply for this loan
        </Button>
      </FormFieldset>
    );
  }
);
